import React from 'react';
import { useIngresos } from '../../../modules/ingresos/hooks/useIngresos';
import { useEgresos } from '../../../modules/egresos/hooks/useEgresos';
import { useIngresosOptimistic } from '../../../modules/ingresos/hooks/useIngresosOptimistic';
import { useEgresosOptimistic } from '../../../modules/egresos/hooks/useEgresosOptimistic';
import { LoggerService } from '../../../shared/services/logger.service';
import AdvancedChart from '../../../components/feature/AdvancedChart';

export const SimpleMainDashboard: React.FC = () => {
  const { getTotalIngresos } = useIngresos();
  const { getTotalEgresos } = useEgresos();
  
  // Obtener datos reales de Firebase
  const { ingresos, isLoading: ingresosLoading } = useIngresosOptimistic();
  const { egresos, isLoading: egresosLoading } = useEgresosOptimistic();

  // Log dashboard view
  React.useEffect(() => {
    LoggerService.logUserAction('main_dashboard_viewed', 'SimpleMainDashboard');
  }, []);

  const totalIngresos = getTotalIngresos();
  const totalEgresos = getTotalEgresos();
  const balanceTotal = totalIngresos - totalEgresos;
  const tasaAhorro = totalIngresos > 0 ? ((balanceTotal / totalIngresos) * 100) : 0;
  
  // Procesar datos reales de ingresos agrupados por categoría
  const ingresosData = React.useMemo(() => {
    if (!ingresos || ingresos.length === 0) {
      return []; // Sin datos reales
    }

    const groupedIngresos = ingresos.reduce((acc, ingreso) => {
      const categoria = ingreso.categoria || 'Sin categoría';
      if (!acc[categoria]) {
        acc[categoria] = 0;
      }
      acc[categoria] += ingreso.monto;
      return acc;
    }, {} as Record<string, number>);

    const colors = ['#059669', '#10b981', '#34d399', '#6ee7b7', '#86efac'];
    return Object.entries(groupedIngresos).map(([categoria, total], index) => ({
      label: categoria,
      value: total,
      color: colors[index % colors.length]
    }));
  }, [ingresos]);

  // Procesar datos reales de egresos agrupados por categoría
  const egresosData = React.useMemo(() => {
    if (!egresos || egresos.length === 0) {
      return []; // Sin datos reales
    }

    const groupedEgresos = egresos.reduce((acc, egreso) => {
      const categoria = egreso.categoria || 'Sin categoría';
      if (!acc[categoria]) {
        acc[categoria] = 0;
      }
      acc[categoria] += egreso.monto;
      return acc;
    }, {} as Record<string, number>);

    const colors = ['#dc2626', '#ef4444', '#f87171', '#fca5a5', '#991b1b'];
    return Object.entries(groupedEgresos).map(([categoria, total], index) => ({
      label: categoria,
      value: total,
      color: colors[index % colors.length]
    }));
  }, [egresos]);

  const ingresoPromedio = ingresosData.length > 0 ? totalIngresos / ingresosData.length : 0;
  const gastoPromedio = egresosData.length > 0 ? totalEgresos / egresosData.length : 0;

  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      {/* Balance Principal */}
      <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl p-6 text-white shadow-lg">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-medium opacity-90">Balance Mensual</h2>
            <p className="text-4xl font-bold">${balanceTotal.toLocaleString()}</p>
            <p className="text-emerald-100 text-sm">
              {balanceTotal >= 0 ? 'Superávit del período' : 'Déficit del período'}
            </p>
          </div>
          <div className="bg-white bg-opacity-20 p-3 rounded-full">
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>

      {/* Indicador de estado de datos */}
      {(ingresosLoading || egresosLoading) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-blue-800">Cargando datos financieros...</p>
          </div>
        </div>
      )}

      {(!ingresosLoading && !egresosLoading && ingresosData.length === 0 && egresosData.length === 0) && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6 text-center">
          <div className="text-4xl mb-2">📊</div>
          <h3 className="text-lg font-bold text-yellow-800 mb-2">No hay datos financieros</h3>
          <p className="text-yellow-700">Agrega tus primeros ingresos y gastos para ver las estadísticas.</p>
        </div>
      )}

      {/* Ingresos y Egresos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ingresos */}
        <div className="space-y-4">
          {ingresosData.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Ingresos</h3>
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">💰</div>
                <p>No hay ingresos registrados</p>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold">Total:</span>
                  <span className="text-2xl font-bold text-emerald-600">${totalIngresos.toLocaleString()}</span>
                </div>
              </div>
            </div>
          ) : (
            <AdvancedChart 
              data={ingresosData} 
              type="income"
              title="Ingresos"
              allowLineChart={true}
            />
          )}
        </div>

        {/* Egresos */}
        <div className="space-y-4">
          {egresosData.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Egresos</h3>
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2">💸</div>
                <p>No hay egresos registrados</p>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold">Total:</span>
                  <span className="text-2xl font-bold text-red-600">${totalEgresos.toLocaleString()}</span>
                </div>
              </div>
            </div>
          ) : (
            <AdvancedChart 
              data={egresosData} 
              type="expense"
              title="Egresos"
              allowLineChart={true}
            />
          )}
        </div>
      </div>

      {/* Comparativo Ingresos vs Egresos */}
      <div className="bg-white rounded-xl shadow-lg p-6 mt-8">
        <h3 className="text-xl font-bold text-gray-800 mb-6">Comparativo Ingresos vs Egresos</h3>
        
        <div className="space-y-4">
          {/* Barra Ingresos */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 w-20">
              <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
              <span className="text-sm font-medium text-gray-700">Ingresos</span>
            </div>
            <div className="flex-1">
              <div className="bg-gray-200 rounded-full h-6 relative overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-emerald-400 to-emerald-500 h-full rounded-full transition-all duration-500"
                  style={{ width: '100%' }}
                ></div>
              </div>
            </div>
            <span className="text-lg font-bold text-emerald-600 w-32 text-right">${totalIngresos.toLocaleString()}</span>
          </div>

          {/* Barra Egresos */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 w-20">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="text-sm font-medium text-gray-700">Egresos</span>
            </div>
            <div className="flex-1">
              <div className="bg-gray-200 rounded-full h-6 relative overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-red-400 to-red-500 h-full rounded-full transition-all duration-500"
                  style={{ width: `${totalIngresos > 0 ? (totalEgresos / totalIngresos) * 100 : 0}%` }}
                ></div>
              </div>
            </div>
            <span className="text-lg font-bold text-red-600 w-32 text-right">${totalEgresos.toLocaleString()}</span>
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="flex justify-between items-center">
            <div>
              <span className="text-lg font-bold text-gray-700">Balance:</span>
              <p className="text-sm text-gray-500">Tasa de ahorro: {tasaAhorro.toFixed(1)}%</p>
            </div>
            <span className={`text-2xl font-bold ${balanceTotal >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              ${balanceTotal.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {/* Análisis Detallado */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        {/* Análisis de Ingresos */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-emerald-700 mb-4 flex items-center gap-2">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
            </svg>
            Análisis de Ingresos
          </h3>
          
          {ingresosData.length > 0 ? (
            <div className="space-y-4">
              {/* Top 3 Categorías */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Top 3 Fuentes de Ingresos</h4>
                {ingresosData
                  .sort((a, b) => b.value - a.value)
                  .slice(0, 3)
                  .map((item, index) => (
                    <div key={index} className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}</span>
                        <span className="font-medium">{item.label}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-emerald-600">${item.value.toLocaleString()}</div>
                        <div className="text-sm text-gray-500">
                          {((item.value / totalIngresos) * 100).toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              {/* Métricas */}
              <div className="bg-emerald-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-emerald-700">Ingreso Promedio</div>
                    <div className="font-bold text-emerald-800">${ingresoPromedio.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm text-emerald-700">Fuentes Activas</div>
                    <div className="font-bold text-emerald-800">{ingresosData.length}</div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">💰</div>
              <p>No hay ingresos registrados</p>
              <p className="text-sm">Agrega tus primeros ingresos para ver el análisis</p>
            </div>
          )}
        </div>

        {/* Análisis de Egresos */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-red-700 mb-4 flex items-center gap-2">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
            </svg>
            Análisis de Gastos
          </h3>
          
          {egresosData.length > 0 ? (
            <div className="space-y-4">
              {/* Top 3 Gastos */}
              <div>
                <h4 className="font-semibold text-gray-700 mb-2">Mayores Gastos</h4>
                {egresosData
                  .sort((a, b) => b.value - a.value)
                  .slice(0, 3)
                  .map((item, index) => (
                    <div key={index} className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{index === 0 ? '🔴' : index === 1 ? '🟠' : '🟡'}</span>
                        <span className="font-medium">{item.label}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-red-600">${item.value.toLocaleString()}</div>
                        <div className="text-sm text-gray-500">
                          {((item.value / totalEgresos) * 100).toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  ))}
              </div>

              {/* Métricas */}
              <div className="bg-red-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-red-700">Gasto Promedio</div>
                    <div className="font-bold text-red-800">${gastoPromedio.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm text-red-700">Categorías</div>
                    <div className="font-bold text-red-800">{egresosData.length}</div>
                  </div>
                </div>
              </div>

              {/* Recomendación */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-yellow-600">💡</span>
                  <span className="font-medium text-yellow-800">Recomendación</span>
                </div>
                <p className="text-sm text-yellow-700">
                  {balanceTotal < 0 
                    ? `Considera reducir gastos en ${egresosData.sort((a, b) => b.value - a.value)[0]?.label || 'la categoría principal'}`
                    : `¡Excelente! Podrías aumentar tus ahorros en $${balanceTotal.toLocaleString()}`
                  }
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <div className="text-4xl mb-2">💸</div>
              <p>No hay gastos registrados</p>
              <p className="text-sm">Agrega tus gastos para ver el análisis</p>
            </div>
          )}
        </div>
      </div>

      {/* Comparativo Avanzado */}
      {(ingresosData.length > 0 || egresosData.length > 0) && (
        <div className="bg-white rounded-xl shadow-lg p-6 mt-8">
          <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
            </svg>
            Análisis Comparativo Detallado
          </h3>
          
          {/* Tabla de Comparación */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 font-semibold text-gray-700">Métrica</th>
                  <th className="text-right py-3 font-semibold text-emerald-700">Ingresos</th>
                  <th className="text-right py-3 font-semibold text-red-700">Egresos</th>
                  <th className="text-right py-3 font-semibold text-blue-700">Diferencia</th>
                </tr>
              </thead>
              <tbody className="space-y-2">
                <tr className="border-b border-gray-100">
                  <td className="py-3">Total</td>
                  <td className="py-3 text-right font-medium text-emerald-600">${totalIngresos.toLocaleString()}</td>
                  <td className="py-3 text-right font-medium text-red-600">${totalEgresos.toLocaleString()}</td>
                  <td className={`py-3 text-right font-bold ${balanceTotal >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    ${Math.abs(balanceTotal).toLocaleString()}
                  </td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3">Promedio por categoría</td>
                  <td className="py-3 text-right">${ingresosData.length > 0 ? ingresoPromedio.toLocaleString() : '0'}</td>
                  <td className="py-3 text-right">${egresosData.length > 0 ? gastoPromedio.toLocaleString() : '0'}</td>
                  <td className="py-3 text-right font-medium">
                    {ingresosData.length > 0 && egresosData.length > 0 
                      ? `${ingresoPromedio > gastoPromedio ? '+' : ''}${(ingresoPromedio - gastoPromedio).toLocaleString()}`
                      : '-'
                    }
                  </td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3">Número de categorías</td>
                  <td className="py-3 text-right">{ingresosData.length}</td>
                  <td className="py-3 text-right">{egresosData.length}</td>
                  <td className="py-3 text-right">{Math.abs(ingresosData.length - egresosData.length)}</td>
                </tr>
                <tr>
                  <td className="py-3 font-medium">Tasa de ahorro</td>
                  <td className="py-3 text-right" colSpan={2}>
                    <span className={`font-bold ${tasaAhorro >= 20 ? 'text-emerald-600' : tasaAhorro >= 10 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {tasaAhorro.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-3 text-right text-sm text-gray-500">
                    {tasaAhorro >= 20 ? '¡Excelente!' : tasaAhorro >= 10 ? 'Bueno' : 'Mejorable'}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Botón de Talk with Us */}
      <div className="fixed bottom-6 right-6">
        <button className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 font-medium">
          Talk with Us
        </button>
      </div>
    </div>
  );
};
